import { isNil } from "es-toolkit";
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";

import { CookieKeys } from "@/utils/cookie-keys";

export const setClientCookie = (key: string, value?: string | null) => {
  if (isNil(value) || value === "") {
    Cookies.remove(key);
  } else {
    Cookies.set(key, value, { expires: 365 * 10, priority: "high" });
  }

  const event = new Event("cookieChange");
  window.dispatchEvent(event); // Trigger custom event for cookie changes
};

export const useGetClientCookiesValues = () => {
  const [publicKey, setPublicKey] = useState(
    Cookies.get(CookieKeys.publicKey) || "",
  );
  const [initializedCookies, setInitializedCookies] = useState(false);

  useEffect(() => {
    const handleCookieChange = () => {
      setPublicKey(Cookies.get(CookieKeys.publicKey) || "");
    };

    // Listen to custom 'cookieChange' event
    window.addEventListener("cookieChange", handleCookieChange);

    setInitializedCookies(true);
    return () => {
      window.removeEventListener("cookieChange", handleCookieChange);
    };
  }, []);

  return { publicKey, initializedCookies };
};

export const useCookieState = <T>(key: string, defaultValue: T) => {
  const state = useState<T>(defaultValue);
  const [value, setValue] = state;
  const didSetInitial = useRef(false);

  useEffect(() => {
    const rawCookieValue = Cookies.get(key);
    const parsedCookieValue = rawCookieValue
      ? JSON.parse(rawCookieValue)
      : undefined;

    // Default values don't need to be stored in cookies
    // On initial run we don't want to remove the cookie immediately
    if (value === defaultValue && didSetInitial.current) {
      Cookies.remove(key);
    }

    // If there is a cookie value the current state is the default value, set the state to the cookie value
    if (!didSetInitial.current && parsedCookieValue !== undefined) {
      setValue(parsedCookieValue);
    }

    // Update cookie if value changed
    if (value !== defaultValue) {
      Cookies.set(key, JSON.stringify(value), {
        expires: 365 * 10,
        priority: "high",
      });
    }

    didSetInitial.current = true;
  }, [key, defaultValue, value]);

  return state;
};
